import { useEffect } from 'react'
import { Alert, App, AutoComplete, Form, Input, InputNumber, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { SiteSelect } from '@/features/sites/SiteSelect'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  NAS_SECRET_MIN_LENGTH,
  createNas,
  isValidNasName,
  updateNas,
  type NasEntry,
} from './nasApi'

interface NasFormModalProps {
  open: boolean
  /** `null` = yangi qurilma */
  nas: NasEntry | null
  onClose: () => void
}

interface FormValues {
  nasname: string
  shortname: string
  type?: string
  ports?: number | null
  secret?: string
  server?: string
  community?: string
  description?: string
  siteId?: string | null
}

/**
 * FreeRADIUS `nas.type` uchun ko'p uchraydigan qiymatlar.
 *
 * ⚠️ RO'YXAT YOPIQ EMAS (`AutoComplete`, `Select` emas): `type` FreeRADIUS
 * lug'atidagi qiymat va u o'rnatmaga qarab kengayadi. Yopiq ro'yxat
 * operatorni bazasidagi mavjud qiymatni saqlay olmaydigan holatga
 * tushirardi — tahrirlashda esa u qiymat JIMGINA `other` ga almashib
 * ketardi (backend bo'sh `type` ni `other` qiladi).
 */
const TYPE_SUGGESTIONS = ['other', 'cisco', 'livingston', 'portslave', 'patton'] as const

/**
 * NAS qo'shish va tahrirlash.
 *
 * ⚠️ IKKI HAR XIL SO'ROV, bitta forma: yaratishda `secret` MAJBURIY
 * (`POST /api/nas`), tahrirlashda esa maydonning O'ZI YO'Q
 * (`PUT /api/nas/{id}` — `NasUpdate` da sir yo'q). Sirni almashtirish
 * alohida amal (`NasSecretModal`).
 *
 * Sabab `NasDraft` javadoc'ida: oddiy tahrir (tavsif, port) sirni qayta
 * yuborishga majbur qilmasligi kerak. Aks holda panel sirni qayerdadir
 * saqlab yurishi kerak bo'lardi — aynan qilmaslik kerak bo'lgan narsa.
 */
export function NasFormModal({ open, nas, onClose }: NasFormModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      nas
        ? {
            nasname: nas.nasname,
            shortname: nas.shortname,
            type: nas.type ?? 'other',
            ports: nas.ports,
            server: nas.server ?? '',
            community: nas.community ?? '',
            description: nas.description ?? '',
            siteId: nas.siteId,
          }
        : { type: 'other', siteId: null },
    )
  }, [open, nas, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      // Bo'sh satr `null` ga aylantiriladi: baza bo'sh satrni "qiymat bor"
      // deb saqlardi va kartochkada bo'sh katak "sozlangan" bo'lib
      // ko'rinardi
      const common = {
        nasname: values.nasname.trim(),
        shortname: values.shortname.trim(),
        type: values.type?.trim() || null,
        ports: values.ports ?? null,
        server: values.server?.trim() || null,
        community: values.community?.trim() || null,
        description: values.description?.trim() || null,
        // ⚠️ `undefined` EMAS, `null`: tanlov tozalanganda joylashuv bog'lanishi
        // UZILISHI kerak. `undefined` JSON'da umuman yuborilmasdi va backend
        // eski qiymatni saqlab qolardi — operator esa uni o'chirdim deb o'ylardi.
        siteId: values.siteId ?? null,
      }
      return nas
        ? updateNas(nas.id, common)
        : createNas({ ...common, secret: values.secret?.trim() ?? '' })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.nas.all })
      // X-SIB-004 — muvaffaqiyatli amaldan keyin ANIQ tasdiq: oyna
      // yopiladi va ekranda hech qanday iz qolmasdi
      void message.success(nas ? t('common.toastSaved') : t('common.toastCreated'))
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={nas ? t('nas.editTitle') : t('nas.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        {/* Yaratish oynasi OQIBATNI oldindan aytadi: bu yozuv abonentning
            umuman ulana olishini belgilaydi, "yana bitta qator" emas */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={nas ? t('nas.editNote') : t('nas.createNote')}
        />

        <Form.Item
          name="nasname"
          label={t('nas.nasname')}
          extra={t('nas.nasnameHint')}
          rules={[
            { required: true, message: t('nas.validation.nasnameRequired') },
            {
              validator: (_, value: string | undefined) =>
                !value || isValidNasName(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('nas.validation.nasnameFormat'))),
            },
          ]}
        >
          <Input autoFocus placeholder="10.10.20.2" />
        </Form.Item>

        <Form.Item
          name="shortname"
          label={t('nas.shortname')}
          extra={t('nas.shortnameHint')}
          rules={[{ required: true, message: t('nas.validation.shortnameRequired') }]}
        >
          <Input placeholder="kabul-tower-01" />
        </Form.Item>

        {/* Sir FAQAT yaratishda. Tahrirlashda maydon umuman chizilmaydi —
            bo'sh qoldirilgan "ixtiyoriy sir" maydoni "bo'sh = o'zgarmaydi"
            va "bo'sh = tozalanadi" degan ikki xil o'qilardi */}
        {nas === null ? (
          <Form.Item
            name="secret"
            label={t('nas.secret')}
            extra={t('nas.secretCreateHint')}
            rules={[
              { required: true, message: t('nas.validation.secretRequired') },
              {
                min: NAS_SECRET_MIN_LENGTH,
                message: t('nas.validation.secretLength', { min: NAS_SECRET_MIN_LENGTH }),
              },
            ]}
          >
            {/* ⚠️ `Input.Password` `visibilityToggle` SIZ: kiritilayotgan
                sirni ekranda ochish imkoniyati parol darajasidagi qiymat
                uchun keraksiz xavf (yelka orqali o'qish, ekran yozib olish) */}
            <Input.Password visibilityToggle={false} autoComplete="new-password" />
          </Form.Item>
        ) : null}

        <Form.Item name="type" label={t('nas.type')} extra={t('nas.typeHint')}>
          <AutoComplete options={TYPE_SUGGESTIONS.map((value) => ({ value }))} />
        </Form.Item>

        {/* ⚠️ `min={1}`: nol yuborilmasligi kerak. Backend `NULL` va `0` ni
            ajrata olmaydi (`nasApi.ts` dagi `toNas` izohi), shuning uchun
            panel `0` yozsa bazadagi "sozlanmagan" holat qaytarib
            bo'lmaydigan tarzda `0` ga aylanardi. */}
        <Form.Item name="ports" label={t('nas.ports')} extra={t('nas.portsHint')}>
          <InputNumber min={1} style={{ width: '100%' }} placeholder={t('common.noValue')} />
        </Form.Item>

        {/* ⚠️ `server` — VIRTUAL SERVER NOMI (FreeRADIUS `sites-enabled`
            dagi bo'lim), IP EMAS (`NasEntry` javadoc'i). Shuning uchun
            bu yerda IP tekshiruvi YO'Q — u to'g'ri qiymatni rad etardi. */}
        <Form.Item name="server" label={t('nas.server')} extra={t('nas.serverHint')}>
          <Input placeholder={t('common.noValue')} />
        </Form.Item>

        <Form.Item name="community" label={t('nas.community')} extra={t('nas.communityHint')}>
          <Input placeholder={t('common.noValue')} />
        </Form.Item>

        {/* ⚠️ JOYLASHUV — texnik maydon emas, EKSPLUATATSION. `nasname` bu
            qurilma bilan qanday GAPLASHISHNI aytadi, lekin uni QAYERDA
            izlashni aytmaydi. Uzilish paytida kerak bo'ladigan javob shu. */}
        <Form.Item name="siteId" label={t('nas.site')} extra={t('nas.siteHint')}>
          <SiteSelect />
        </Form.Item>

        <Form.Item name="description" label={t('nas.description')}>
          <Input.TextArea rows={2} maxLength={200} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
